import React from 'react';
import styled from 'styled-components';
import { useLanguage } from '../contexts/LanguageContext';

/**
 * Privacy Policy page - Required by GDPR (Article 13)
 */

const PageContainer = styled.main`
  max-width: 900px;
  margin: 120px auto 80px;
  padding: 0 ${props => props.theme.spacing[6]};
  
  @media (max-width: ${props => props.theme.breakpoints.md}) {
    margin: 100px auto 60px;
    padding: 0 ${props => props.theme.spacing[4]};
  }
`;

const PageTitle = styled.h1`
  font-size: ${props => props.theme.typography.sizes['4xl']};
  font-weight: ${props => props.theme.typography.weights.bold};
  color: ${props => props.theme.colors.text};
  margin-bottom: ${props => props.theme.spacing[4]};
  
  @media (max-width: ${props => props.theme.breakpoints.md}) {
    font-size: ${props => props.theme.typography.sizes['3xl']};
  }
`;

const LastUpdate = styled.p`
  font-size: ${props => props.theme.typography.sizes.sm};
  color: ${props => props.theme.colors.textSecondary};
  margin-bottom: ${props => props.theme.spacing[8]};
  font-style: italic;
`;

const Section = styled.section`
  margin-bottom: ${props => props.theme.spacing[10]};
`;

const SectionTitle = styled.h2`
  font-size: ${props => props.theme.typography.sizes['2xl']};
  font-weight: ${props => props.theme.typography.weights.semibold};
  color: ${props => props.theme.colors.text};
  margin-bottom: ${props => props.theme.spacing[4]};
  padding-bottom: ${props => props.theme.spacing[2]};
  border-bottom: 2px solid ${props => props.theme.colors.primary[500]};
`;

const SubTitle = styled.h3`
  font-size: ${props => props.theme.typography.sizes.xl};
  font-weight: ${props => props.theme.typography.weights.medium};
  color: ${props => props.theme.colors.text};
  margin-top: ${props => props.theme.spacing[6]};
  margin-bottom: ${props => props.theme.spacing[3]};
`;

const Paragraph = styled.p`
  font-size: ${props => props.theme.typography.sizes.base};
  line-height: ${props => props.theme.typography.lineHeights.relaxed};
  color: ${props => props.theme.colors.textSecondary};
  margin-bottom: ${props => props.theme.spacing[4]};
  
  strong {
    color: ${props => props.theme.colors.text};
    font-weight: ${props => props.theme.typography.weights.semibold};
  }
`;

const List = styled.ul`
  margin-left: ${props => props.theme.spacing[6]};
  margin-bottom: ${props => props.theme.spacing[4]};
  
  li {
    font-size: ${props => props.theme.typography.sizes.base};
    line-height: ${props => props.theme.typography.lineHeights.relaxed};
    color: ${props => props.theme.colors.textSecondary};
    margin-bottom: ${props => props.theme.spacing[2]};
  }
`;

const HighlightBox = styled.div`
  background-color: ${props => props.theme.colors.primary[50]};
  border-left: 4px solid ${props => props.theme.colors.primary[500]};
  padding: ${props => props.theme.spacing[4]};
  border-radius: ${props => props.theme.borderRadius.lg};
  margin-bottom: ${props => props.theme.spacing[4]};
`;

const Link = styled.a`
  color: ${props => props.theme.colors.primary[600]};
  text-decoration: none;
  
  &:hover {
    text-decoration: underline;
  }
`;

const PrivacyPolicy: React.FC = () => {
  const { language } = useLanguage();

  return (
    <PageContainer>
      <PageTitle>
        {language === 'fr' ? 'Politique de Confidentialité' : 'Privacy Policy'}
      </PageTitle>
      <LastUpdate>
        {language === 'fr' 
          ? 'Dernière mise à jour : 1er octobre 2025' 
          : 'Last updated: October 1, 2025'}
      </LastUpdate>

      <Section>
        <SectionTitle>
          {language === 'fr' ? '1. Introduction' : '1. Introduction'}
        </SectionTitle>
        <Paragraph>
          {language === 'fr'
            ? "La présente politique de confidentialité décrit comment Nadir Benadda collecte, utilise et protège vos données personnelles lorsque vous utilisez ce site web (ci-après « le Site »)."
            : "This privacy policy describes how Nadir Benadda collects, uses and protects your personal data when you use this website (hereinafter 'the Site')."}
        </Paragraph>
        <Paragraph>
          {language === 'fr'
            ? "Conformément au Règlement Général sur la Protection des Données (RGPD) et à la loi Informatique et Libertés, vous disposez de droits sur vos données personnelles."
            : "In accordance with the General Data Protection Regulation (GDPR) and the Data Protection Act, you have rights over your personal data."}
        </Paragraph>
      </Section>

      <Section>
        <SectionTitle>
          {language === 'fr' ? '2. Responsable du traitement' : '2. Data Controller'}
        </SectionTitle>
        <HighlightBox>
          <Paragraph>
            <strong>{language === 'fr' ? 'Responsable :' : 'Controller:'}</strong> Nadir Benadda
          </Paragraph>
          <Paragraph>
            <strong>{language === 'fr' ? 'Contact :' : 'Contact:'}</strong>{' '}
            <Link href="mailto:n.benadda.dev@gmail.com">n.benadda.dev@gmail.com</Link>
          </Paragraph>
        </HighlightBox>
      </Section>

      <Section>
        <SectionTitle>
          {language === 'fr' ? '3. Données collectées' : '3. Data Collected'}
        </SectionTitle>
        
        <SubTitle>
          {language === 'fr' ? '3.1. Formulaire de contact' : '3.1. Contact Form'}
        </SubTitle>
        <Paragraph>
          {language === 'fr'
            ? "Lorsque vous utilisez notre formulaire de contact, nous collectons les données suivantes :"
            : "When you use our contact form, we collect the following data:"}
        </Paragraph>
        <List>
          <li>{language === 'fr' ? 'Nom et prénom' : 'Name and surname'}</li>
          <li>{language === 'fr' ? 'Adresse e-mail' : 'Email address'}</li>
          <li>{language === 'fr' ? 'Sujet du message' : 'Message subject'}</li>
          <li>{language === 'fr' ? 'Contenu du message' : 'Message content'}</li>
        </List>

        <SubTitle>
          {language === 'fr' ? '3.2. Données de navigation' : '3.2. Browsing Data'}
        </SubTitle>
        <Paragraph>
          {language === 'fr'
            ? "Le Site peut automatiquement collecter certaines données techniques :"
            : "The Site may automatically collect certain technical data:"}
        </Paragraph>
        <List>
          <li>{language === 'fr' ? "Adresse IP (via l'hébergeur Vercel)" : "IP address (via hosting provider Vercel)"}</li>
          <li>{language === 'fr' ? 'Type de navigateur' : 'Browser type'}</li>
          <li>{language === 'fr' ? 'Pages consultées' : 'Pages viewed'}</li>
          <li>{language === 'fr' ? 'Date et heure de visite' : 'Date and time of visit'}</li>
        </List>

        <SubTitle>
          {language === 'fr' ? '3.3. Google Fonts' : '3.3. Google Fonts'}
        </SubTitle>
        <Paragraph>
          {language === 'fr'
            ? "Ce site utilise Google Fonts. Lors du chargement des polices, votre adresse IP peut être transmise aux serveurs de Google. Pour plus d'informations, consultez la "
            : "This site uses Google Fonts. When loading fonts, your IP address may be transmitted to Google's servers. For more information, see "}
          <Link href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer">
            {language === 'fr' ? 'politique de confidentialité de Google' : "Google's privacy policy"}
          </Link>.
        </Paragraph>
      </Section>

      <Section>
        <SectionTitle>
          {language === 'fr' ? '4. Finalités du traitement' : '4. Processing Purposes'}
        </SectionTitle>
        <Paragraph>
          {language === 'fr'
            ? 'Vos données personnelles sont collectées pour les finalités suivantes :'
            : 'Your personal data is collected for the following purposes:'}
        </Paragraph>
        <List>
          <li>
            <strong>{language === 'fr' ? 'Formulaire de contact :' : 'Contact form:'}</strong>{' '}
            {language === 'fr'
              ? "Répondre à vos demandes d'information et de contact"
              : 'Respond to your information and contact requests'}
          </li>
          <li>
            <strong>{language === 'fr' ? 'Sécurité :' : 'Security:'}</strong>{' '}
            {language === 'fr'
              ? "Détecter et prévenir les activités malveillantes (scraping, spam)"
              : 'Detect and prevent malicious activities (scraping, spam)'}
          </li>
          <li>
            <strong>{language === 'fr' ? 'Amélioration du site :' : 'Site improvement:'}</strong>{' '}
            {language === 'fr'
              ? 'Analyser le trafic et améliorer la performance du site'
              : 'Analyze traffic and improve site performance'}
          </li>
        </List>
      </Section>

      <Section>
        <SectionTitle>
          {language === 'fr' ? '5. Base légale du traitement' : '5. Legal Basis for Processing'}
        </SectionTitle>
        <Paragraph>
          {language === 'fr'
            ? 'Le traitement de vos données repose sur les bases légales suivantes :'
            : 'The processing of your data is based on the following legal bases:'}
        </Paragraph>
        <List>
          <li>
            <strong>{language === 'fr' ? 'Consentement :' : 'Consent:'}</strong>{' '}
            {language === 'fr'
              ? 'Pour le formulaire de contact (case à cocher obligatoire)'
              : 'For the contact form (mandatory checkbox)'}
          </li>
          <li>
            <strong>{language === 'fr' ? 'Intérêt légitime :' : 'Legitimate interest:'}</strong>{' '}
            {language === 'fr'
              ? 'Pour la sécurité et la prévention de la fraude'
              : 'For security and fraud prevention'}
          </li>
        </List>
      </Section>

      <Section>
        <SectionTitle>
          {language === 'fr' ? '6. Durée de conservation' : '6. Retention Period'}
        </SectionTitle>
        <Paragraph>
          {language === 'fr'
            ? 'Vos données sont conservées pour les durées suivantes :'
            : 'Your data is kept for the following periods:'}
        </Paragraph>
        <List>
          <li>
            <strong>{language === 'fr' ? 'Messages de contact :' : 'Contact messages:'}</strong>{' '}
            {language === 'fr'
              ? '3 ans à compter de la dernière interaction'
              : '3 years from the last interaction'}
          </li>
          <li>
            <strong>{language === 'fr' ? 'Logs techniques :' : 'Technical logs:'}</strong>{' '}
            {language === 'fr'
              ? "Maximum 12 mois (conservés par l'hébergeur)"
              : 'Maximum 12 months (kept by the hosting provider)'}
          </li>
        </List>
      </Section>

      <Section>
        <SectionTitle>
          {language === 'fr' ? '7. Destinataires des données' : '7. Data Recipients'}
        </SectionTitle>
        <Paragraph>
          {language === 'fr'
            ? 'Vos données peuvent être transmises aux destinataires suivants :'
            : 'Your data may be transmitted to the following recipients:'}
        </Paragraph>
        <List>
          <li>
            <strong>Nadir Benadda</strong> -{' '}
            {language === 'fr'
              ? 'Administrateur du site'
              : 'Site administrator'}
          </li>
          <li>
            <strong>Vercel</strong> -{' '}
            {language === 'fr'
              ? "Hébergeur du site (USA) - Voir leur "
              : 'Site host (USA) - See their '}
            <Link href="https://vercel.com/legal/privacy-policy" target="_blank" rel="noopener noreferrer">
              {language === 'fr' ? 'politique de confidentialité' : 'privacy policy'}
            </Link>
          </li>
          <li>
            <strong>Google</strong> -{' '}
            {language === 'fr'
              ? 'Pour Google Fonts uniquement (adresse IP)'
              : 'For Google Fonts only (IP address)'}
          </li>
        </List>
      </Section>

      <Section>
        <SectionTitle>
          {language === 'fr' ? '8. Vos droits' : '8. Your Rights'}
        </SectionTitle>
        <Paragraph>
          {language === 'fr'
            ? 'Conformément au RGPD, vous disposez des droits suivants :'
            : 'In accordance with GDPR, you have the following rights:'}
        </Paragraph>
        <List>
          <li>
            <strong>{language === 'fr' ? "Droit d'accès :" : 'Right of access:'}</strong>{' '}
            {language === 'fr'
              ? 'Obtenir une copie de vos données personnelles'
              : 'Obtain a copy of your personal data'}
          </li>
          <li>
            <strong>{language === 'fr' ? 'Droit de rectification :' : 'Right to rectification:'}</strong>{' '}
            {language === 'fr'
              ? 'Corriger vos données inexactes'
              : 'Correct your inaccurate data'}
          </li>
          <li>
            <strong>{language === 'fr' ? "Droit à l'effacement :" : 'Right to erasure:'}</strong>{' '}
            {language === 'fr'
              ? 'Supprimer vos données (« droit à l\'oubli »)'
              : 'Delete your data ("right to be forgotten")'}
          </li>
          <li>
            <strong>{language === 'fr' ? 'Droit à la limitation :' : 'Right to restriction:'}</strong>{' '}
            {language === 'fr'
              ? 'Limiter le traitement de vos données'
              : 'Limit the processing of your data'}
          </li>
          <li>
            <strong>{language === 'fr' ? "Droit d'opposition :" : 'Right to object:'}</strong>{' '}
            {language === 'fr'
              ? "Vous opposer au traitement de vos données"
              : 'Object to the processing of your data'}
          </li>
          <li>
            <strong>{language === 'fr' ? 'Droit à la portabilité :' : 'Right to data portability:'}</strong>{' '}
            {language === 'fr'
              ? 'Récupérer vos données dans un format structuré'
              : 'Retrieve your data in a structured format'}
          </li>
        </List>

        <HighlightBox>
          <Paragraph>
            <strong>{language === 'fr' ? 'Pour exercer vos droits :' : 'To exercise your rights:'}</strong>
          </Paragraph>
          <Paragraph>
            {language === 'fr'
              ? "Envoyez un e-mail à "
              : 'Send an email to '}
            <Link href="mailto:n.benadda.dev@gmail.com">n.benadda.dev@gmail.com</Link>
            {language === 'fr'
              ? " avec votre demande. Vous recevrez une réponse dans un délai d'un mois."
              : ' with your request. You will receive a response within one month.'}
          </Paragraph>
        </HighlightBox>

        <Paragraph>
          {language === 'fr'
            ? "Vous avez également le droit d'introduire une réclamation auprès de la CNIL (Commission Nationale de l'Informatique et des Libertés) :"
            : "You also have the right to lodge a complaint with the CNIL (French Data Protection Authority):"}
        </Paragraph>
        <Paragraph>
          <Link href="https://www.cnil.fr" target="_blank" rel="noopener noreferrer">
            www.cnil.fr
          </Link>
        </Paragraph>
      </Section>

      <Section>
        <SectionTitle>
          {language === 'fr' ? '9. Sécurité des données' : '9. Data Security'}
        </SectionTitle>
        <Paragraph>
          {language === 'fr'
            ? "Nous mettons en œuvre des mesures de sécurité appropriées pour protéger vos données contre tout accès non autorisé, altération, divulgation ou destruction :"
            : "We implement appropriate security measures to protect your data against unauthorized access, alteration, disclosure or destruction:"}
        </Paragraph>
        <List>
          <li>{language === 'fr' ? 'Chiffrement HTTPS' : 'HTTPS encryption'}</li>
          <li>{language === 'fr' ? 'Protection anti-scraping' : 'Anti-scraping protection'}</li>
          <li>{language === 'fr' ? 'Hébergement sécurisé (Vercel)' : 'Secure hosting (Vercel)'}</li>
          <li>{language === 'fr' ? 'Obfuscation des emails' : 'Email obfuscation'}</li>
        </List>
      </Section>

      <Section>
        <SectionTitle>
          {language === 'fr' ? '10. Cookies' : '10. Cookies'}
        </SectionTitle>
        <Paragraph>
          {language === 'fr'
            ? "Ce site n'utilise actuellement aucun cookie de suivi, d'analyse ou publicitaire. Aucun consentement n'est donc requis."
            : "This site currently does not use any tracking, analytics or advertising cookies. No consent is therefore required."}
        </Paragraph>
        <Paragraph>
          {language === 'fr'
            ? "Si des cookies venaient à être utilisés à l'avenir, un bandeau de consentement conforme au RGPD sera mis en place."
            : "If cookies are used in the future, a GDPR-compliant consent banner will be implemented."}
        </Paragraph>
      </Section>

      <Section>
        <SectionTitle>
          {language === 'fr' ? '11. Transferts de données hors UE' : '11. Data Transfers Outside EU'}
        </SectionTitle>
        <Paragraph>
          {language === 'fr'
            ? "Vos données peuvent être transférées vers les États-Unis via nos prestataires (Vercel, Google Fonts). Ces transferts sont encadrés par des garanties appropriées (clauses contractuelles types approuvées par la Commission européenne)."
            : "Your data may be transferred to the United States via our service providers (Vercel, Google Fonts). These transfers are governed by appropriate safeguards (standard contractual clauses approved by the European Commission)."}
        </Paragraph>
      </Section>

      <Section>
        <SectionTitle>
          {language === 'fr' ? '12. Modifications de la politique' : '12. Policy Changes'}
        </SectionTitle>
        <Paragraph>
          {language === 'fr'
            ? "Cette politique de confidentialité peut être mise à jour. La date de dernière mise à jour est indiquée en haut de cette page. Nous vous encourageons à consulter régulièrement cette page."
            : "This privacy policy may be updated. The date of last update is shown at the top of this page. We encourage you to regularly check this page."}
        </Paragraph>
      </Section>

      <Section>
        <SectionTitle>
          {language === 'fr' ? '13. Contact' : '13. Contact'}
        </SectionTitle>
        <Paragraph>
          {language === 'fr'
            ? "Pour toute question concernant cette politique de confidentialité ou l'exercice de vos droits, contactez-nous :"
            : "For any questions about this privacy policy or exercising your rights, contact us:"}
        </Paragraph>
        <HighlightBox>
          <Paragraph>
            <strong>Email :</strong>{' '}
            <Link href="mailto:n.benadda.dev@gmail.com">n.benadda.dev@gmail.com</Link>
          </Paragraph>
        </HighlightBox>
      </Section>
    </PageContainer>
  );
};

export default PrivacyPolicy;
